// --------------------- Import Globais ---------------------
import { useState, useEffect, useRef, useMemo } from "react";
import { useMediaQuery } from "@mui/material";
import { BotoesAcoes } from "../../views/ViewGerenciarPostagens/styles";
import { useLocation } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";

// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Row,
  Button,
  MenuProps,
  Skeleton,
  Table,
  Typography,
  Space,
  Dropdown,
} from "antd";
import type { TableProps } from "antd/es/table";

// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

// --------------------- Import Styles ---------------------
import { Container } from "../ViewGerenciarPostagens/styles";

// --------------------- Import Hooks ---------------------
import { useBuscarEtiquetaPessoa } from "./hooks/useBuscarEtiquetaPessoa";
import { useExportar } from "./hooks/useExportar";
import { useMarcarEtiqueta } from "./hooks/useMarcarEtiqueta";

// --------------------- Import Components ---------------------
import { Columns } from "./components/Columns";

// --------------------- Import Uteis ---------------------
import { usePermissao } from "../../uteis/permissaoAdm/usePermissao";
import { useError } from "../../uteis/mensagens/useError";
import { useSuccess } from "../../uteis/mensagens/useSuccess";
import { useWarning } from "../../uteis/mensagens/useWarning";
import LoadingSpin from "../../uteis/LoadingSpin";

// --------------------- Import Constants ---------------------
import { getMenuItems } from "./constants/itemsActionColumns";

// --------------------- Import Interfaces ---------------------
import { Leads } from "../../interfaces/Leads";
import { useBuscarPessoas } from "./hooks/useBuscarPessoa";
// ---------------------  ---------------------



interface LocationState {
  etiquetaId?: number;
}
export default function ViewGerenciarEtiquetaPessoa() {
  const location = useLocation();

  const etiquetaId = (location.state as LocationState)?.etiquetaId;

  



  // Traz o estilo Title da biblioteca
  const { Title } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const emLote = useRef<number>(0);

  // Verificação de permissão do usuário, NDA = 0 - SEM PERMISSÃO, NDA = 1 - COM PERMISSÃO
  // Definação do Loading caso seja permitido
  const { isLoading: isTokenLoading, isValid } = usePermissao("OPE");

  // Armazena o valor do id da pessoa selecionada
  const [pessoaId, setPessoaId] = useState<number | null>(null);

  const [nomeUsuario, setNomeUsuario] = useState<string | null>(null);
  const [tipoPessoa, setTipoPessoa] = useState<string | null>(null);

  // Controle de Paginação
  const [currentPage, setCurrentPage] = useState(1);

  const { pessoas, isLoading, fetch } = useBuscarPessoas();
  const { etiquetaPessoa, isLoading: isLoadingEtiqueta } = useBuscarEtiquetaPessoa();

  // Inicializando o filtro
  const [filteredInfo, setFilteredInfo] = useState<{
    nomeUsuario?: string[] | null;
    tipoPessoa?: string[] | null;
    celular?: string[] | null;
  }>({
    nomeUsuario: null,
    tipoPessoa: null,
    celular: null,
  });

  const [semFiltros, setSemFiltros] = useState(true);

  useEffect(() => {
  if (!etiquetaId || !etiquetaPessoa.length || !pessoas.length) return;

  // pessoas que possuem a etiqueta atual
  const pessoasComEtiquetaAtual = etiquetaPessoa
    .filter((ep) => ep.etiquetaId === etiquetaId)
    .map((ep) => ep.pessoaId); // ids de Pessoa

  // IDs usados na tabela (rowKey = "id")
  const idsSelecionados = pessoas
    .filter((p) => pessoasComEtiquetaAtual.includes(p.id))
    .map((p) => p.id);

  setSelectedRowKeys(idsSelecionados);
}, [etiquetaId, etiquetaPessoa, pessoas]);


  useEffect(() => {
    const filtros = localStorage.getItem("filtrosPropriedades");
    const pagina = localStorage.getItem("pageGerPropriedades");

    if (filtros) {
      const novoFiltro = JSON.parse(filtros);
      setFilteredInfo({
        nomeUsuario: novoFiltro.nomeUsuario ? [novoFiltro.nomeUsuario] : null,
        tipoPessoa: novoFiltro.tipoPessoa ? [novoFiltro.tipoPessoa] : null,
        celular: novoFiltro.celular ? [novoFiltro.celular] : null,
      });
      setSemFiltros(false);
    }

    if (pagina) {
      setCurrentPage(Number(pagina));
    }
  }, []);

  const onChange: TableProps<any>["onChange"] = (pagination, filters) => {
    setFilteredInfo({
      nomeUsuario: filters.nomeUsuario as string[] | null,
      tipoPessoa: filters.tipoPessoa as string[] | null,
      celular: filters.celular as string[] | null,
    });

    const hasFilters = Boolean(filters.nomeUsuario || filters.tipoPessoa || filters.celular);
    setSemFiltros(!hasFilters);

    if (pagination.current) {
      localStorage.setItem(
        "pageGerPropriedades",
        pagination.current.toString()
      );
    }

    localStorage.setItem("filtrosPropriedades", JSON.stringify(filters));
  };

  const removerFiltros = () => {
    localStorage.removeItem("filtrosPropriedades");
    localStorage.removeItem("pageGerPropriedades");

    setFilteredInfo({
      nomeUsuario: null,
      tipoPessoa: null,
      celular: null,
    });
    setSemFiltros(true);
    setCurrentPage(1);
  };

  const handleFilter = () => {
    removerFiltros();
  };
  const allIds = useMemo(() => {
    const filteredLeads = pessoas.filter((p) => {
      return (
        // Verificando se o filtro 'nomeUsuario' é um array e utilizando 'some' caso seja
        (!filteredInfo.nomeUsuario ||
          filteredInfo.nomeUsuario.some((nomeUsuario) =>
            p.nomeUsuario.includes(nomeUsuario)
          )) &&
        // Verificando se o filtro 'tipoPessoa' é um array e utilizando 'some' caso seja
        (!filteredInfo.tipoPessoa ||
          filteredInfo.tipoPessoa.some((tipoPessoa) =>
            p.tipoPessoa.includes(tipoPessoa)
          ))&&
      (!filteredInfo.celular || // ✅ novo
        filteredInfo.celular.some((celular) =>
          String(p.celular ?? "").includes(celular)
        ))
      );
    });

    return filteredLeads.map((ep: any) => ep.id);
  }, [pessoas, filteredInfo]);

  const handleSelectAllClick = () => {
    if (selectedRowKeys.length === allIds.length) {
      setSelectedRowKeys([]);
    } else {
      setSelectedRowKeys(allIds);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  emLote.current = selectedRowKeys.length > 1 ? 1 : 0;

  const hasSelected = selectedRowKeys.length > 0;

  const {
    exportar,
    error,
    success,
    warning,
    resetError,
    resetSuccess,
    resetWarning,
  } = useExportar();

  const {
    marcarEtiqueta,
    error: errorMc,
    success: successMc,
    warning: warningMc,
    resetError: resetErrorMc,
    resetSuccess: resetSuccessMc,
    resetWarning: resetWarningMc,
    isLoading: isLoadingMC,
  } = useMarcarEtiqueta();

  // Exibe a Mensagem de  Erro
  const { showError, contextHolder: errorContextHolder } = useError();

  // Exibe a Mensagem de  Sucesso
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();

  // Exibe a Mensagem de Alerta
  const { showWarning, contextHolder: warningContextHolder } = useWarning();

  // Estados para controlar as exibições
  const [errorDisplayed, setErrorDisplayed] = useState(false);
  const [successDisplayed, setSuccessDisplayed] = useState(false);
  const [warningDisplayed, setWarningDisplayed] = useState(false);
  const [warningDisplayedMc, setWarningDisplayedMc] = useState(false);
  const [errorDisplayedMc, setErrorDisplayedMc] = useState(false);
  const [successDisplayedMc, setSuccessDisplayedMc] = useState(false);

  useEffect(() => {
    // Exibe a mensagem de sucesso
    if (success && !successDisplayed) {
      showSuccess(success);
      setSuccessDisplayed(true);

      // Limpa estado de sucesso após 3 segundos e recarrega a página
      setTimeout(() => {
        setSuccessDisplayed(false);
        resetSuccess();
      }, 3000);
    }

    if (successMc && !successDisplayedMc) {
      showSuccess(successMc);
      setSuccessDisplayedMc(true);
      fetch();
      // Limpa estado de sucesso após 3 segundos e recarrega a página
      setTimeout(() => {
        setSuccessDisplayedMc(false);
        resetSuccessMc();
      }, 3000);
    }

    // Exibe a mensagem de erro
    if (error && !errorDisplayed) {
      showError(error);
      setErrorDisplayed(true);

      // Limpa estado de error após 3 segundos
      setTimeout(() => {
        setErrorDisplayed(false);
        resetError();
      }, 3000);
    }

    // Exibe a mensagem de erro
    if (errorMc && !errorDisplayedMc) {
      showError(errorMc);
      setErrorDisplayedMc(true);

      // Limpa estado de error após 3 segundos
      setTimeout(() => {
        setErrorDisplayedMc(false);
        resetErrorMc();
      }, 3000);
    }
    // Exibe a mensagem de alerta
    if (warning && !warningDisplayed) {
      showWarning(warning);
      setWarningDisplayed(true);

      // Limpa estado de error após 3 segundos
      setTimeout(() => {
        setWarningDisplayed(false);
        resetWarning();
      }, 3000);
    }
    // Exibe a mensagem de alerta
    if (warningMc && !warningDisplayedMc) {
      showWarning(warningMc);
      setWarningDisplayedMc(true);

      // Limpa estado de error após 3 segundos
      setTimeout(() => {
        setWarningDisplayedMc(false);
        resetWarningMc();
      }, 3000);
    }
  }, [
    success,
    successMc,
    error,
    errorMc,
    warning,
    warningMc,
    showSuccess,
    showError,
    showWarning,
    successDisplayedMc,
    errorDisplayedMc,
    errorDisplayed,
    successDisplayed,
    warningDisplayed,
    warningDisplayedMc,
    resetSuccessMc,
    resetErrorMc,
    resetError,
    resetSuccess,
    resetWarning,
    resetWarningMc,
    fetch,
  ]);

  // OnClick das opções e funções que serão realizadas do item Ações
  const onClick: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      // Exportar
      case "1":
        if (emLote.current === 1) {
          // Filtrar os dados com base nas chaves selecionadas e extrair apenas nomeUsuario e telefone
          const dadosSelecionados = pessoas
            .filter((ep) => selectedRowKeys.includes(ep.id))
            .map(({ nomeUsuario, tipoPessoa }) => ({
              nomeUsuario,
              tipoPessoa,
            })); // Pegando apenas os campos desejados

          exportar(dadosSelecionados);
        } else {
          exportar([
            {
              nomeUsuario: nomeUsuario,
              tipoPessoa: tipoPessoa,
            },
          ]);
        }
        break;

      case "2":
        if (emLote.current === 1) {
          // Filtrar os dados com base nas chaves selecionadas (row IDs)
          const dados = pessoas
            .filter((p) => selectedRowKeys.includes(p.id)) // seleciona linhas
            .map(({ id }) => ({ pessoaId: id })); // envia como pessoaId

          marcarEtiqueta(dados, etiquetaId);
        } else {
          marcarEtiqueta([{ pessoaId }], etiquetaId); // Garantindo que seja um array
        }
        break;
    }
  };

  const columns = Columns(onClick, setPessoaId, setNomeUsuario, setTipoPessoa, {
    nomeUsuario: filteredInfo.nomeUsuario,
    tipoPessoa: filteredInfo.tipoPessoa,
     celular: filteredInfo.celular,
  });

  return (
    <>
      {(isLoading || isLoadingEtiqueta || isTokenLoading) || !isValid ? (
        <Skeleton active />
      ) : isLoadingMC ? (
        <LoadingSpin
          message="Marcando Etiqueta..."
          description="Esta ação pode levar alguns instantes. Aguarde, por favor..."
        />
      ) : (
        <Container>
          <>{successContextHolder}</>
          <>{errorContextHolder}</>
          <>{warningContextHolder}</>
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/gerenciarEtiquetas`}>
                  <ArrowBackIcon color="primary" />
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Etiqueta Pessoa
                </Title>
              </Flex>
            </Col>
          </Row>
          <Row>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "auto",
                width: "90%",
                gap: "20px",
              }}
            >
              <Space size="middle" style={{ marginLeft: "50px" }}>
                <Button
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "auto",
                  }}
                  onClick={handleSelectAllClick}
                  type="primary"
                >
                  {selectedRowKeys.length === allIds.length
                    ? "Desmarcar todos "
                    : "Selecionar todos "}
                </Button>
                <Dropdown
                  menu={{ items: getMenuItems(), onClick }}
                  trigger={["click"]}
                >
                  <BotoesAcoes>
                    <Space>
                      <SettingsIcon />
                    </Space>
                  </BotoesAcoes>
                </Dropdown>
              </Space>

              <span>
                {hasSelected ? `Selecionado ${selectedRowKeys.length}` : ""}
              </span>
              <div style={{ display: "flex", gap: "20px" }}>
                {semFiltros ? (
                  <Button onClick={handleFilter} type="primary" disabled={true}>
                    <FilterAltIcon />
                  </Button>
                ) : (
                  <Button onClick={handleFilter} type="primary">
                    <FilterAltOffIcon />
                  </Button>
                )}
              </div>
            </div>

            <Col span={isMobile ? 5 : 1}></Col>
            <Col
              span={22}
              style={{
                marginTop: isMobile ? 15 : 15,
                marginBottom: isMobile ? 30 : 50,
              }}
            >
              <Flex
                style={{
                  width: isMobile ? "auto" : "100%",
                  overflow: "auto",
                  marginLeft: 30,
                }}
              >
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={pessoas}
                  onChange={onChange}
                  bordered
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: isMobile ? "auto" : "100%",
                    maxWidth: isMobile ? "none" : "100%",
                  }}
                  pagination={{ defaultCurrent: currentPage }}
                  rowSelection={{ ...rowSelection }}
                />
              </Flex>
            </Col>
            <Col span={1}></Col>
          </Row>
        </Container>
      )}
      ;
    </>
  );
}
